/**
 * HID keyboard-wedge scanner + manual tag injection for the web app.
 * Zebra readers in HID keyboard mode type EPCs as keystrokes (usually ending with Enter).
 */

export type TagReadHandler = (tagId: string) => void
export type KeyActivityHandler = (info: { key: string; at: number; buffer: string }) => void

type WedgeState = {
  armed: boolean
  buffer: string
  lastKeyAt: number
  idleTimer: ReturnType<typeof setTimeout> | null
  listeners: Set<TagReadHandler>
  activityListeners: Set<KeyActivityHandler>
  keydownHandler: ((e: KeyboardEvent) => void) | null
}

const CHAR_IDLE_MS = 140
const GAP_RESET_MS = 2500
const MAX_TAG_LEN = 128

const state: WedgeState = {
  armed: false,
  buffer: '',
  lastKeyAt: 0,
  idleTimer: null,
  listeners: new Set(),
  activityListeners: new Set(),
  keydownHandler: null,
}

function emitTag(raw: string) {
  const tagId = raw.trim().toUpperCase()
  if (!tagId || tagId.length > MAX_TAG_LEN) return
  state.listeners.forEach((cb) => {
    try {
      cb(tagId)
    } catch {
      /* ignore listener errors */
    }
  })
}

function flushBuffer() {
  if (state.idleTimer) {
    clearTimeout(state.idleTimer)
    state.idleTimer = null
  }
  const buf = state.buffer
  state.buffer = ''
  if (buf) emitTag(buf)
}

function notifyActivity(key: string) {
  const info = { key, at: Date.now(), buffer: state.buffer }
  state.activityListeners.forEach((cb) => {
    try {
      cb(info)
    } catch {
      /* ignore */
    }
  })
}

/** Leave human typing alone in normal form fields; only capture elsewhere / scan-capture. */
function shouldIgnoreTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  // Dedicated scan capture field must still feed the wedge buffer.
  if (target.dataset.scanCapture === 'true') return false
  if (target.closest('[data-scan-capture="true"]')) return false
  if (target.closest('[data-no-wedge="true"]')) return true

  const tag = target.tagName
  if (tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) return true
  if (tag === 'INPUT') {
    const type = (target as HTMLInputElement).type || 'text'
    // Allow typing in textual inputs; buttons/checkboxes remain free for wedge capture.
    const textual = new Set([
      'text',
      'search',
      'email',
      'tel',
      'url',
      'password',
      'number',
      'date',
      'datetime-local',
      'time',
      'month',
      'week',
      '',
    ])
    if (textual.has(type.toLowerCase())) return true
  }
  if (target.closest('textarea, select, [contenteditable="true"]')) return true
  if (
    target.closest(
      'input:not([type]), input[type="text"], input[type="search"], input[type="email"], input[type="tel"], input[type="url"], input[type="password"], input[type="number"], input[type="date"], input[type="datetime-local"], input[type="time"], input[type="month"], input[type="week"]'
    )
  ) {
    return true
  }
  return false
}

function appendChar(ch: string) {
  const now = Date.now()
  if (state.lastKeyAt && now - state.lastKeyAt > GAP_RESET_MS) {
    state.buffer = ''
  }
  state.lastKeyAt = now
  state.buffer += ch
  if (state.buffer.length > MAX_TAG_LEN) {
    state.buffer = state.buffer.slice(-MAX_TAG_LEN)
  }
  if (state.idleTimer) clearTimeout(state.idleTimer)
  state.idleTimer = setTimeout(flushBuffer, CHAR_IDLE_MS)
  notifyActivity(ch)
}

function onKeyDown(e: KeyboardEvent) {
  if (!state.armed) return
  if (shouldIgnoreTarget(e.target)) return

  // Ignore pure modifier keys
  if (e.key === 'Shift' || e.key === 'Control' || e.key === 'Alt' || e.key === 'Meta') {
    return
  }

  if (e.key === 'Enter' || e.key === 'Tab') {
    e.preventDefault()
    e.stopPropagation()
    notifyActivity(e.key)
    flushBuffer()
    return
  }

  if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    e.preventDefault()
    e.stopPropagation()
    appendChar(e.key)
  }
}

function ensureListener() {
  if (state.keydownHandler) return
  state.keydownHandler = onKeyDown
  window.addEventListener('keydown', state.keydownHandler, true)
}

function removeListener() {
  if (!state.keydownHandler) return
  window.removeEventListener('keydown', state.keydownHandler, true)
  state.keydownHandler = null
}

export function armHidWedge(): void {
  state.armed = true
  state.buffer = ''
  ensureListener()
}

export function disarmHidWedge(): void {
  state.armed = false
  state.buffer = ''
  if (state.idleTimer) {
    clearTimeout(state.idleTimer)
    state.idleTimer = null
  }
  if (state.listeners.size === 0 && state.activityListeners.size === 0) {
    removeListener()
  }
}

export function isHidWedgeArmed(): boolean {
  return state.armed
}

/** Inject a tag as if scanned (manual entry / mock). */
export function injectTag(tagId: string): void {
  emitTag(tagId)
}

/** Feed characters from a focused capture input (backup path). */
export function ingestCaptureText(text: string, opts?: { submit?: boolean }): void {
  if (!state.armed) return
  const cleaned = text.replace(/[\r\n\t]+/g, '')
  if (!cleaned && opts?.submit) {
    flushBuffer()
    return
  }
  if (!cleaned) return
  state.buffer = cleaned
  state.lastKeyAt = Date.now()
  notifyActivity(`input:${cleaned.slice(-8)}`)
  if (opts?.submit) flushBuffer()
  else {
    if (state.idleTimer) clearTimeout(state.idleTimer)
    state.idleTimer = setTimeout(flushBuffer, CHAR_IDLE_MS)
  }
}

export function subscribeTagReads(handler: TagReadHandler): () => void {
  state.listeners.add(handler)
  ensureListener()
  return () => {
    state.listeners.delete(handler)
    if (
      state.listeners.size === 0 &&
      state.activityListeners.size === 0 &&
      !state.armed
    ) {
      removeListener()
    }
  }
}

/** Observe raw keystrokes while armed (diagnostics). */
export function subscribeKeyActivity(handler: KeyActivityHandler): () => void {
  state.activityListeners.add(handler)
  ensureListener()
  return () => {
    state.activityListeners.delete(handler)
    if (
      state.listeners.size === 0 &&
      state.activityListeners.size === 0 &&
      !state.armed
    ) {
      removeListener()
    }
  }
}
